import { FC, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import classnames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { graphql, useFragment } from 'react-relay';
import {
    useAuctionPricing,
    useAuctionState,
    useAuctionSecondChanceInfo,
} from 'dibs-auctions/exports/hooks';
import { useAuctionBidModalState } from '../hooks/useAuctionBidModalState';
import { trackAuctionBidClick } from 'dibs-auctions-bidding-modal/exports/bidModalTrackingHelpers';
import { updateRelayVariables } from '../../actions/sharedRelayVariableActions';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';

// components
import { PdpSharedAuctionClosed } from '../PdpSharedAuctionClosed/PdpSharedAuctionClosed';
import { PdpSharedAuctionPreview } from '../PdpSharedAuctionPreview/PdpSharedAuctionPreview';
import { PdpSharedAuctionActionBid } from '../PdpSharedAuctionActions/PdpSharedAuctionActionBid';
import { PdpSharedAuctionUnavailable } from '../PdpSharedAuctionUnavailable/PdpSharedAuctionUnavailable';
import { PdpSharedAuctionSubscription } from '../PdpSharedAuctionSubscription/PdpSharedAuctionSubscription';
import { PdpSharedAuctionActionBinAndBid } from '../PdpSharedAuctionActions/PdpSharedAuctionActionBinAndBid';
import { PdpSharedAuctionActionCompletePurchase } from '../PdpSharedAuctionActions/PdpSharedAuctionActionCompletePurchase';
import { PdpSharedAuctionSecondChanceOffer } from '../PdpSharedAuctionActions/PdpSharedAuctionSecondChanceOffer';
import AuctionBiddingModal from 'dibs-auctions-bidding-modal/exports/AuctionBiddingModal';
import { BID_RESPONSE_ERRORS } from 'dibs-auctions-bidding-modal/exports/constants';

// types
import { PdpSharedAuctionInfo_item$key as Item } from './__generated__/PdpSharedAuctionInfo_item.graphql';
import { OnBidPlaced } from 'dibs-auctions-bidding-modal/exports/types';

// styles
import styles from './PdpSharedAuctionInfo.scss';

type Props = {
    item: Item;
    currency: string;
    promoCode: ReactNode;
};

type RelayVariables = {
    userId: string;
    isClient: boolean;
};

type ReduxStoreType = {
    relayVariables: {
        variables: RelayVariables;
    };
};

const itemFragment = graphql`
    fragment PdpSharedAuctionInfo_item on Item {
        isSold
        serviceId
        isUnpurchasable
        relevantAuctionLot(page: pdp) {
            endDate
        }
        relevantAuctionSolution(page: pdp) {
            reserveMet
            highestBidderId
        }
        lastTransaction(buyerId: $userId) @include(if: $fetchUser) {
            isCanceled
            checkoutDate
            isWinningAuctionOrder
            isSecondChanceOfferActive
            mostRecentOffer {
                id
                serviceId
                isExpired
            }
        }
        ...useAuctionSecondChanceInfo_item @arguments(page: pdp)
        ...PdpSharedAuctionSubscription_item
        ...PriceDisplay_item @arguments(userId: $userId, isTrade: $isTrade)
        ...useAuctionState_item @arguments(page: pdp)
        ...useAuctionPricing_item @arguments(page: pdp)
        ...PdpSharedAuctionActionBinAndBid_item
        ...PdpSharedAuctionActionBid_item
        ...PdpSharedAuctionClosed_item
        ...PdpSharedAuctionActionCompletePurchase_item
        ...PdpSharedAuctionPreview_item
        ...PdpSharedAuctionSecondChanceOffer_item
        ...bidModalTrackingHelpers_item
    }
`;

export const PdpSharedAuctionInfo: FC<Props> = ({ currency, item: itemRef, promoCode }) => {
    const item = useFragment(itemFragment, itemRef);
    const {
        isPreviewAuction,
        isActiveAuction,
        isPausedAuction,
        isTransitionAuction,
        isUnavailableAuction,
        hasAuctionEndedWithBIN,
        hasAuctionEndedWithWinner,
        hasAuctionEndedWithNoWinner,
    } = useAuctionState({ item });
    const { isSecondChanceOfferSent } = useAuctionSecondChanceInfo({ item });
    const { isBuyItNowEligible } = useAuctionPricing({
        item,
        currency,
    });
    const { userId, isClient } = useSelector<ReduxStoreType, RelayVariables>(
        relay => relay?.relayVariables?.variables || {}
    );
    const { isMobile, isPdpSidebarRemovePaidShippingVariant } = useContext(ServerVarsContext);
    const dispatch = useDispatch();
    const {
        isSold,
        lastTransaction,
        relevantAuctionLot,
        relevantAuctionSolution,
        isUnpurchasable,
    } = item || {};

    const {
        mostRecentOffer,
        isWinningAuctionOrder,
        isSecondChanceOfferActive,
        checkoutDate: transactionCheckoutDate,
        isCanceled: isTransactionCanceled,
    } = lastTransaction || {};
    const { endDate } = relevantAuctionLot || {};
    const { reserveMet, highestBidderId } = relevantAuctionSolution || {};
    const itemIsUnpurchasable =
        isUnavailableAuction ||
        (hasAuctionEndedWithBIN && !isSold) || // item was sold with BIN and then canceled
        isUnpurchasable;

    const [isBiddingDisabled, setIsBiddingDisabled] = useState<boolean>(
        isTransitionAuction || false
    );
    const { isModalOpen, openModal, closeModal } = useAuctionBidModalState();

    const onAuctionBidClick = (): void => {
        trackAuctionBidClick({ item });
        openModal();
    };

    const [isCloseToReserve, setIsCloseToReserve] = useState(false);

    const onBidPlaced: OnBidPlaced = bidError => {
        if (bidError.errorType === BID_RESPONSE_ERRORS.CLOSE_TO_RESERVE) {
            setIsCloseToReserve(true);
        } else if (isCloseToReserve) {
            setIsCloseToReserve(false);
        }
    };

    const refetch = useCallback(() => {
        dispatch(updateRelayVariables({ updateAuctionItem: true }));
    }, [dispatch]);

    // when countdown timer completes PREVIEW state, refresh page to enter ACTIVE state
    const onPreviewCountdownFinished = useCallback(() => {
        refetch();
    }, [refetch]);

    const onActiveCountdownFinished = useCallback(() => {
        refetch();
        setIsBiddingDisabled(true);
    }, [refetch]);

    useEffect(() => {
        if (!isTransitionAuction) {
            setIsBiddingDisabled(false);
        }
    }, [endDate, isTransitionAuction]);

    let content = null;
    let hideBorder = false;

    if (itemIsUnpurchasable) {
        content = <PdpSharedAuctionUnavailable />;
    } else if (isSecondChanceOfferActive && !isSold && !transactionCheckoutDate) {
        content = <PdpSharedAuctionSecondChanceOffer item={item} currency={currency} />;
    } else if (isPreviewAuction) {
        content = (
            <PdpSharedAuctionPreview
                item={item}
                currency={currency}
                onCountdownFinished={onPreviewCountdownFinished}
                promoCode={promoCode}
            />
        );
    } else if ((isActiveAuction || isTransitionAuction) && isBuyItNowEligible) {
        content = (
            <PdpSharedAuctionActionBinAndBid
                item={item}
                currency={currency}
                onAuctionBidClick={onAuctionBidClick}
                isBiddingDisabled={isBiddingDisabled}
                onCountdownFinished={onActiveCountdownFinished}
            />
        );
    } else if (isActiveAuction || isTransitionAuction) {
        content = (
            <PdpSharedAuctionActionBid
                item={item}
                currency={currency}
                onAuctionBidClick={onAuctionBidClick}
                isBiddingDisabled={isBiddingDisabled}
                onCountdownFinished={onActiveCountdownFinished}
                isCloseToReserve={isCloseToReserve}
            />
        );
    } else if (isPausedAuction || isSold || isSecondChanceOfferSent) {
        content = (
            <PdpSharedAuctionClosed
                item={item}
                currency={currency}
                linkToAuctions={!reserveMet && !isSecondChanceOfferSent}
                linkToBidHistory={!!reserveMet && !isSecondChanceOfferSent}
            />
        );
        hideBorder = true;
    } else if (hasAuctionEndedWithWinner && isClient) {
        if (
            highestBidderId === userId &&
            (!lastTransaction ||
                (isWinningAuctionOrder &&
                    !transactionCheckoutDate &&
                    !mostRecentOffer?.isExpired &&
                    !isTransactionCanceled))
        ) {
            content = <PdpSharedAuctionActionCompletePurchase item={item} currency={currency} />;
        } else {
            content = <PdpSharedAuctionClosed item={item} currency={currency} linkToBidHistory />;
            hideBorder = true;
        }
    } else if (hasAuctionEndedWithNoWinner) {
        content = <PdpSharedAuctionClosed item={item} currency={currency} linkToAuctions />;
        hideBorder = true;
    }
    return (
        <>
            {content && (
                <div
                    className={classnames(styles.container, {
                        [styles.divider]:
                            !isSold &&
                            !itemIsUnpurchasable &&
                            !hideBorder &&
                            !isPdpSidebarRemovePaidShippingVariant,
                    })}
                >
                    {content}
                </div>
            )}
            {!isPreviewAuction && promoCode}
            <PdpSharedAuctionSubscription refetch={refetch} item={item} />
            {isModalOpen && (
                <AuctionBiddingModal
                    currency={currency}
                    itemId={item?.serviceId || ''}
                    onClose={closeModal}
                    isMobile={!!isMobile}
                    isBiddingDisabled={isBiddingDisabled}
                    onBidPlaced={onBidPlaced}
                />
            )}
        </>
    );
};
